import { Calculator, Lock, LockOpen } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { authFetch, useAuth } from '../auth'
import {
  DualMonthRangeCalendar,
  rangeDisplayLabel,
  type DualMonthRangeValue,
} from '../components/DualMonthRangeCalendar'
import { DataTableWorkbench, type WorkbenchColumn } from '../components/DataTableWorkbench'
import { useProductData } from '../data'
import { usePinduoduoOrderFacts } from '../platform'
import { internalProductCost } from '../productMaster'
import type { JdProductCatalog } from '../platform'
import { pinduoduoStatisticsWindow } from '../pinduoduoStatisticsWindow'
import { statisticsWindow, type StatisticsPeriod } from '../statistics'
import type {
  CompanyProductOverrideRecord,
  CompanyProductOverrides,
  ErpInventory,
  ErpInventoryRecord,
  InternalProductInfo,
  InternalProductInfoRecord,
  InventoryCostMode,
  PlatformProductMapping,
  ProductImageOverride,
  ProductImageOverrides,
} from '../types'
import { metricsAcrossDates, money } from '../utils'

type CompanySalesPeriod = Extract<StatisticsPeriod, 'day' | 'last7' | 'last30'> | 'custom'

const COMPANY_SALES_PERIOD_OPTIONS: Array<{ key: Exclude<CompanySalesPeriod, 'custom'>; label: string }> = [
  { key: 'day', label: '昨日' },
  { key: 'last7', label: '最近7天' },
  { key: 'last30', label: '最近30天' },
]

const erpSalesForPeriod = (record: ErpInventoryRecord | undefined, period: CompanySalesPeriod) => {
  if (!record || period === 'custom') return null
  if (period === 'day') return record.salesYesterday
  if (period === 'last7') return record.salesWeek
  return record.salesMonth
}

const shanghaiToday = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date())

const fieldValue = (value: string | number | null) =>
  value === null || value === '' ? '—' : String(value)

interface LinkedVariant {
  id: string
  platformId: string
  storeId: string
  platformProductId: string
  name: string
  merchantSku: string
  image: string
  imageLabel: string
}

const calculatorTargetByStore: Record<string, { label: string; pageKey: string; route: string }> = {
  'jd-588485': { label: '京东旗舰店', pageKey: 'jd.profit_calculator', route: '/jd/profit-calculator' },
  'pdd-beijuxiong-youhe': { label: '拼多多优合店', pageKey: 'pdd.profit_calculator', route: '/pinduoduo/profit-calculator' },
}

const calculatorUrl = (variant: LinkedVariant) => {
  const target = calculatorTargetByStore[variant.storeId]
  return target ? `${target.route}?sku=${encodeURIComponent(variant.id)}` : ''
}

interface ProductReference {
  jdSkuId: string
  title: string
  image: string
  imageLabel: string
}

const imageUrl = (path: string) => path ? `/${path.replace(/^\/+/, '')}` : ''
const inventoryCostModeLabel = (mode: InventoryCostMode) => mode === 'brand-special' ? '特价库存' : '总代库存'
const companyAccessoryFor = (
  records: InternalProductInfoRecord[],
  override?: CompanyProductOverrideRecord,
) => override?.isAccessory ?? records.some((record) =>
  record.productName.includes('配件')
  || (typeof record.ecommercePrice === 'number' && record.ecommercePrice > 0 && record.ecommercePrice < 30),
)
const companyInventoryCostMode = (
  override: CompanyProductOverrideRecord | undefined,
  fallback: InventoryCostMode,
) => {
  const businessDate = shanghaiToday()
  const operations = [...(override?.inventoryCostOperations || [])]
    .filter((operation) => operation.effectiveDate <= businessDate)
    .sort((left, right) => right.effectiveDate.localeCompare(left.effectiveDate) || right.changedAt.localeCompare(left.changedAt))
  return operations[0]?.mode || fallback
}
const readFileAsDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader()
  reader.addEventListener('load', () => resolve(String(reader.result || '')))
  reader.addEventListener('error', () => reject(new Error('图片读取失败')))
  reader.readAsDataURL(file)
})

interface CompanyDraft {
  isAccessory: boolean
  inventoryCostMode: InventoryCostMode
}

export function InternalProductInfoView({
  companyData,
  companyOverrides,
  imageOverrides,
  platformMappings,
  jdCatalog,
  erpInventory,
  reloadCompanyOverrides,
  reloadImageOverrides,
}: {
  companyData?: InternalProductInfo
  companyOverrides?: CompanyProductOverrides
  imageOverrides?: ProductImageOverrides
  platformMappings?: PlatformProductMapping[]
  jdCatalog?: JdProductCatalog
  erpInventory?: ErpInventory
  reloadCompanyOverrides?: () => Promise<void>
  reloadImageOverrides?: () => Promise<void>
} = {}) {
  const auth = useAuth()
  const navigate = useNavigate()
  const productContext = useProductData()
  const pddOrders = usePinduoduoOrderFacts()
  const data = productContext.data
  const selectedDate = productContext.selectedDate
  const internalProductInfo = companyData || productContext.internalProductInfo
  const [editUnlocked, setEditUnlocked] = useState(false)
  const [drafts, setDrafts] = useState<Map<string, CompanyDraft>>(() => new Map())
  const [saving, setSaving] = useState(false)
  const [uploadingProductCode, setUploadingProductCode] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [salesPeriod, setSalesPeriod] = useState<CompanySalesPeriod>('day')
  const [customRange, setCustomRange] = useState<DualMonthRangeValue>({ start: '', end: '' })
  const [calendarOpen, setCalendarOpen] = useState(false)
  const calendarLatestDate = useMemo(() => {
    const candidates = [
      selectedDate,
      data?.meta.dates.at(-1),
      pddOrders.data?.meta.queryEnd,
      shanghaiToday(),
    ].filter((value): value is string => Boolean(value))
    return candidates.sort().at(-1) || shanghaiToday()
  }, [data?.meta.dates, pddOrders.data?.meta.queryEnd, selectedDate])
  const salesPeriodLabel = salesPeriod === 'custom' && customRange.start && customRange.end
    ? rangeDisplayLabel(customRange)
    : (COMPANY_SALES_PERIOD_OPTIONS.find((item) => item.key === salesPeriod)?.label || '昨日')

  const jdReferences = useMemo<LinkedVariant[]>(() => {
    if (jdCatalog) return jdCatalog.records.map((record) => ({
      id: record.jdSkuId,
      platformId: 'jd',
      storeId: 'jd-588485',
      platformProductId: record.spuId,
      name: record.productName,
      merchantSku: record.merchantSku,
      image: imageUrl(record.imagePath),
      imageLabel: record.imagePath ? '京东SKU参考图' : '',
    }))
    return (data?.products || []).filter((product) => product.level === 'sku').map((product) => ({
      id: product.id,
      platformId: 'jd',
      storeId: 'jd-588485',
      platformProductId: product.spuId,
      name: product.name,
      merchantSku: product.merchantSku,
      image: product.image,
      imageLabel: product.imageLabel,
    }))
  }, [data, jdCatalog])

  const skusByProductCode = useMemo(() => {
    const index = new Map<string, LinkedVariant[]>()
    if (platformMappings) {
      const businessDate = shanghaiToday()
      const jdBySku = new Map(jdReferences.map((reference) => [reference.id, reference]))
      for (const mapping of platformMappings) {
        if (
          mapping.status !== 'confirmed'
          || mapping.effectiveFrom > businessDate
          || (mapping.effectiveTo !== null && mapping.effectiveTo < businessDate)
        ) continue
        const matches = index.get(mapping.companyVariantId) || []
        const jdReference = mapping.platformId === 'jd' ? jdBySku.get(mapping.platformVariantId) : undefined
        matches.push({
          id: mapping.platformVariantId,
          platformId: mapping.platformId,
          storeId: mapping.storeId,
          platformProductId: mapping.platformProductId || '',
          name: jdReference?.name || '',
          merchantSku: mapping.companyVariantId,
          image: jdReference?.image || '',
          imageLabel: jdReference?.imageLabel || '',
        })
        index.set(mapping.companyVariantId, matches)
      }
      return index
    }
    for (const product of jdReferences) {
      const code = product.merchantSku.trim()
      if (!code || code === '--') continue
      const matches = index.get(code) || []
      matches.push(product)
      index.set(code, matches)
    }
    return index
  }, [jdReferences, platformMappings])

  const referenceByProductCode = useMemo(() => {
    const index = new Map<string, ProductReference>()
    for (const [code, variants] of skusByProductCode) {
      const reference = variants.find((variant) => variant.platformId === 'jd' && variant.image)
        || variants.find((variant) => variant.platformId === 'jd')
      if (!reference) continue
      index.set(code, {
        jdSkuId: reference.id,
        title: reference.name,
        image: reference.image,
        imageLabel: reference.imageLabel,
      })
    }
    return index
  }, [skusByProductCode])

  const codeCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const record of internalProductInfo?.records || []) {
      if (record.productCode) counts.set(record.productCode, (counts.get(record.productCode) || 0) + 1)
    }
    return counts
  }, [internalProductInfo])

  const recordsByProductCode = useMemo(() => {
    const index = new Map<string, InternalProductInfoRecord[]>()
    for (const record of internalProductInfo?.records || []) {
      if (!record.productCode) continue
      const records = index.get(record.productCode) || []
      records.push(record)
      index.set(record.productCode, records)
    }
    return index
  }, [internalProductInfo])

  const companyOverrideByCode = useMemo(() => new Map(
    (companyOverrides?.records || []).map((record) => [record.companyVariantId, record]),
  ), [companyOverrides])

  const companyImageByCode = useMemo(() => {
    const index = new Map<string, ProductImageOverride>()
    for (const [productCode, image] of Object.entries(imageOverrides?.companyImages || {})) {
      if (productCode) index.set(productCode, image)
    }
    for (const image of Object.values(imageOverrides?.images || {})) {
      if (image.productCode && !index.has(image.productCode)) index.set(image.productCode, image)
    }
    return index
  }, [imageOverrides])

  const erpInventoryByProductCode = useMemo(() => new Map(
    (erpInventory?.records || []).map((record) => [record.productCode.trim(), record]),
  ), [erpInventory])

  const jdSalesWindow = useMemo(() => {
    if (!data?.meta.dates.length) return null
    if (salesPeriod === 'custom') {
      if (!customRange.start || !customRange.end) return null
      const startDate = customRange.start
      const endDate = customRange.end
      const dates = data.meta.dates.filter((date) => date >= startDate && date <= endDate)
      const requestedDayCount = Math.round(
        (new Date(`${endDate}T00:00:00Z`).getTime() - new Date(`${startDate}T00:00:00Z`).getTime()) / 86400000,
      ) + 1
      return {
        key: 'custom' as const,
        label: '自定义',
        startDate,
        endDate,
        dates,
        requestedDayCount,
        complete: dates.length === requestedDayCount,
      }
    }
    if (!selectedDate) return null
    return statisticsWindow(salesPeriod, selectedDate, data.meta.dates)
  }, [customRange.end, customRange.start, data, salesPeriod, selectedDate])

  const jdSalesByProductCode = useMemo(() => {
    const result = new Map<string, number>()
    if (!data || !jdSalesWindow?.dates.length) return result
    for (const product of data.products) {
      if (product.level !== 'sku') continue
      const code = product.merchantSku.trim()
      if (!code || code === '--') continue
      const sold = metricsAcrossDates(product, jdSalesWindow.dates).soldItemCount
      if (!sold) continue
      result.set(code, (result.get(code) || 0) + sold)
    }
    return result
  }, [data, jdSalesWindow])

  const pddSalesWindow = useMemo(
    () => pinduoduoStatisticsWindow(
      pddOrders.data,
      salesPeriod,
      salesPeriod === 'custom'
        ? { startDate: customRange.start, endDate: customRange.end }
        : { startDate: '', endDate: '' },
    ),
    [customRange.end, customRange.start, pddOrders.data, salesPeriod],
  )

  const pddSalesByProductCode = useMemo(() => {
    const result = new Map<string, number>()
    if (!pddSalesWindow) return result
    for (const row of pddOrders.data?.dailySku || []) {
      if (row.outcome !== 'confirmed') continue
      if (row.date < pddSalesWindow.startDate || row.date > pddSalesWindow.endDate) continue
      const code = row.skuCode.trim()
      if (!code) continue
      result.set(code, (result.get(code) || 0) + row.soldItemCount)
    }
    return result
  }, [pddOrders.data, pddSalesWindow])

  const matchedRecordCount = useMemo(
    () => (internalProductInfo?.records || []).filter(
      (record) => record.productCode && skusByProductCode.has(record.productCode),
    ).length,
    [internalProductInfo, skusByProductCode],
  )
  const linkedSkuCount = useMemo(
    () => Array.from(skusByProductCode.entries())
      .filter(([code]) => codeCounts.has(code))
      .reduce((total, [, skus]) => total + skus.length, 0),
    [codeCounts, skusByProductCode],
  )
  const imageRecordCount = useMemo(
    () => (internalProductInfo?.records || []).filter(
      (record) => Boolean(record.productCode && (
        companyImageByCode.get(record.productCode)?.path
        || referenceByProductCode.get(record.productCode)?.image
      )),
    ).length,
    [companyImageByCode, internalProductInfo, referenceByProductCode],
  )

  const companyRows = useMemo(() => {
    const records = [...(internalProductInfo?.records || [])]
    if (!jdCatalog) return records
    return records.sort((left, right) => {
      const leftReference = left.productCode ? referenceByProductCode.get(left.productCode) : undefined
      const rightReference = right.productCode ? referenceByProductCode.get(right.productCode) : undefined
      const leftImage = left.productCode ? companyImageByCode.get(left.productCode)?.path || leftReference?.image : ''
      const rightImage = right.productCode ? companyImageByCode.get(right.productCode)?.path || rightReference?.image : ''
      const imageOrder = Number(Boolean(rightImage)) - Number(Boolean(leftImage))
      if (imageOrder) return imageOrder
      const linkOrder = Number(Boolean(rightReference)) - Number(Boolean(leftReference))
      return linkOrder || left.sourceRow - right.sourceRow
    })
  }, [companyImageByCode, internalProductInfo, jdCatalog, referenceByProductCode])

  if (!internalProductInfo) return null
  const hasJdReferenceSource = Boolean(jdCatalog || data)
  const originalDraft = (productCode: string): CompanyDraft => {
    const records = recordsByProductCode.get(productCode) || []
    const override = companyOverrideByCode.get(productCode)
    const cost = internalProductCost(records)
    return {
      isAccessory: companyAccessoryFor(records, override),
      inventoryCostMode: companyInventoryCostMode(override, cost.inventoryCostMode),
    }
  }
  const updateDraft = (productCode: string, patch: Partial<CompanyDraft>) => {
    if (!productCode) return
    setDrafts((current) => {
      const original = originalDraft(productCode)
      const updated = { ...(current.get(productCode) || original), ...patch }
      const next = new Map(current)
      if (
        updated.isAccessory === original.isAccessory
        && updated.inventoryCostMode === original.inventoryCostMode
      ) next.delete(productCode)
      else next.set(productCode, updated)
      return next
    })
    setMessage('')
    setError('')
  }
  const toggleEditLock = async () => {
    if (!editUnlocked) {
      setEditUnlocked(true)
      setMessage('')
      setError('')
      return
    }
    if (!drafts.size) {
      setEditUnlocked(false)
      return
    }
    setSaving(true)
    setMessage('')
    setError('')
    try {
      const response = await authFetch('/api/company-product-overrides', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          records: Array.from(drafts, ([companyVariantId, draft]) => ({ companyVariantId, ...draft })),
        }),
      })
      const payload = await response.json() as { error?: string }
      if (!response.ok) throw new Error(payload.error || '公司产品资料保存失败')
      await reloadCompanyOverrides?.()
      setDrafts(new Map())
      setEditUnlocked(false)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setSaving(false)
    }
  }
  const uploadCompanyImage = async (productCode: string, file: File) => {
    setUploadingProductCode(productCode)
    setMessage('')
    setError('')
    try {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) throw new Error('只支持 JPG、PNG 或 WebP 图片')
      if (!file.size || file.size > 5 * 1024 * 1024) throw new Error('图片为空或超过5MB')
      const dataUrl = await readFileAsDataUrl(file)
      const response = await authFetch('/api/product-images', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productCode, fileName: file.name, dataUrl }),
      })
      const payload = await response.json() as { error?: string }
      if (!response.ok) throw new Error(payload.error || '公司产品图片上传失败')
      await reloadImageOverrides?.()
      setMessage(`已为公司产品 ${productCode} 更新图片。`)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setUploadingProductCode('')
    }
  }

  const linkedVariants = (record: InternalProductInfoRecord) => record.productCode ? skusByProductCode.get(record.productCode) || [] : []
  const calculatorVariants = (record: InternalProductInfoRecord) => linkedVariants(record).filter((variant) => {
    const target = calculatorTargetByStore[variant.storeId]
    return target && auth.canAccess(variant.storeId, target.pageKey)
  })
  const referenceFor = (record: InternalProductInfoRecord) => record.productCode ? referenceByProductCode.get(record.productCode) : undefined
  const companyImageFor = (record: InternalProductInfoRecord) => record.productCode ? companyImageByCode.get(record.productCode) : undefined
  const companyCostFor = (record: InternalProductInfoRecord) => internalProductCost(record.productCode ? recordsByProductCode.get(record.productCode) || [record] : [record])
  const currentDraftFor = (record: InternalProductInfoRecord) => record.productCode ? drafts.get(record.productCode) || originalDraft(record.productCode) : null
  const currentInventoryModeFor = (record: InternalProductInfoRecord) => {
    const cost = companyCostFor(record)
    const override = record.productCode ? companyOverrideByCode.get(record.productCode) : undefined
    return currentDraftFor(record)?.inventoryCostMode || companyInventoryCostMode(override, cost.inventoryCostMode)
  }
  const currentProductCostFor = (record: InternalProductInfoRecord) => {
    const cost = companyCostFor(record)
    return currentInventoryModeFor(record) === 'brand-special' ? cost.brandSpecialPrice : cost.generalAgentPrice
  }
  const companyColumns: Array<WorkbenchColumn<InternalProductInfoRecord>> = [
    { id: 'rowNumber', label: '序号', width: 52, contentWidth: 52, locked: true, filterable: false, sortable: false, getValue: (record) => record.sourceRow, render: (_record, context) => context.rowNumber },
    {
      id: 'image', label: '图片', width: 72, contentWidth: 72, getValue: (record) => companyImageFor(record)?.path || referenceFor(record)?.image || '',
      render: (record) => {
        const companyImage = companyImageFor(record)
        const reference = referenceFor(record)
        const displayImage = companyImage?.path || reference?.image || ''
        return record.productCode ? <label className="company-image-trigger" title={displayImage ? '点击替换公司产品图片' : '点击上传公司产品图片'}>
            <input type="file" accept="image/jpeg,image/png,image/webp" disabled={Boolean(uploadingProductCode)} onChange={(event) => {
              const file = event.currentTarget.files?.[0]
              event.currentTarget.value = ''
              if (file) void uploadCompanyImage(record.productCode, file)
            }} />
            {displayImage ? <img src={displayImage} alt="" loading="lazy" /> : <span className="master-image-fallback">无图</span>}
          </label> : displayImage ? <img className="company-static-image" src={displayImage} alt="" loading="lazy" /> : <span className="master-image-fallback">无图</span>
      },
    },
    {
      id: 'productName', label: '公司品名', width: 260, locked: true,
      getValue: (record) => record.productName,
      render: (record) => <span className="internal-product-name"><strong title={record.productName}>{fieldValue(record.productName)}</strong></span>,
    },
    { id: 'productCode', label: '产品编码', width: 140, getValue: (record) => record.productCode, render: (record) => <span className="internal-code-cell">{fieldValue(record.productCode)}{record.productCode && (codeCounts.get(record.productCode) || 0) > 1 && <small>重复编码</small>}</span> },
    {
      id: 'storeCalculator', label: '店铺测算', width: 180,
      getValue: (record) => calculatorVariants(record).map((variant) => `${calculatorTargetByStore[variant.storeId]?.label || variant.storeId} ${variant.id}`).join(' '),
      render: (record) => {
        const linked = linkedVariants(record)
        const variants = calculatorVariants(record)
        if (!variants.length) return <span className="internal-unmatched">{linked.length ? '无计算器权限' : '未关联店铺SKU'}</span>
        if (variants.length === 1) {
          const variant = variants[0]
          const target = calculatorTargetByStore[variant.storeId]
          return <Link className="company-calculator-link" to={calculatorUrl(variant)} title={`进入${target.label}利润计算器`}><Calculator aria-hidden="true" />{target.label}测算</Link>
        }
        const variantsByStore = new Map<string, LinkedVariant[]>()
        for (const variant of variants) variantsByStore.set(variant.storeId, [...(variantsByStore.get(variant.storeId) || []), variant])
        return <select className="company-calculator-select" defaultValue="" aria-label={`${record.productCode} 选择店铺SKU测算`} onChange={(event) => {
          if (event.target.value) navigate(event.target.value)
        }}>
          <option value="">选择测算（{variants.length}）</option>
          {Array.from(variantsByStore).map(([storeId, storeVariants]) => <optgroup key={storeId} label={calculatorTargetByStore[storeId]?.label || storeId}>
            {storeVariants.map((variant) => <option key={`${variant.storeId}:${variant.id}`} value={calculatorUrl(variant)}>SKU {variant.id}</option>)}
          </optgroup>)}
        </select>
      },
    },
    {
      id: 'erpAvailable', label: 'ERP库存', width: 100, filterType: 'number',
      getValue: (record) => erpInventoryByProductCode.get(record.productCode.trim())?.available,
      render: (record) => {
        const erpRecord = erpInventoryByProductCode.get(record.productCode.trim())
        return <span className={erpRecord?.available === 0 ? 'inventory-figure-danger' : ''} title={erpRecord ? `网店管家ERP可订购数量 · 货品 ${erpRecord.productCode}` : '未按产品编码关联到ERP货品'}>{erpRecord ? erpRecord.available.toLocaleString('zh-CN') : '—'}</span>
      },
    },
    {
      id: 'companySales', label: '公司销量', width: 100, filterType: 'number',
      getValue: (record) => erpSalesForPeriod(erpInventoryByProductCode.get(record.productCode.trim()), salesPeriod),
      render: (record) => {
        if (salesPeriod === 'custom') {
          return <span title="公司销量来自ERP昨日/近7天/近30天快照，自定义区间暂无分天明细">—</span>
        }
        const value = erpSalesForPeriod(erpInventoryByProductCode.get(record.productCode.trim()), salesPeriod)
        return <span title={value === null ? '未按产品编码关联到ERP货品' : `网店管家ERP${salesPeriodLabel}销量（全渠道）`}>{value === null ? '—' : value.toLocaleString('zh-CN')}</span>
      },
    },
    {
      id: 'jdSales', label: '京东销量', width: 100, filterType: 'number',
      getValue: (record) => {
        const code = record.productCode.trim()
        if (!code || !jdSalesWindow?.dates.length) return null
        return jdSalesByProductCode.has(code) ? jdSalesByProductCode.get(code)! : (skusByProductCode.get(code)?.some((item) => item.platformId === 'jd') ? 0 : null)
      },
      render: (record) => {
        const code = record.productCode.trim()
        if (!code || !jdSalesWindow?.dates.length) {
          return <span title={productContext.loading ? '正在加载京东成交数据…' : '京东成交数据暂不可用'}>—</span>
        }
        const hasJd = jdSalesByProductCode.has(code) || skusByProductCode.get(code)?.some((item) => item.platformId === 'jd')
        if (!hasJd) return <span title="未关联京东SKU">—</span>
        const value = jdSalesByProductCode.get(code) || 0
        return <span title={`京东旗舰店确认成交件数 · ${salesPeriodLabel} · ${jdSalesWindow.startDate === jdSalesWindow.endDate ? jdSalesWindow.startDate : `${jdSalesWindow.startDate} 至 ${jdSalesWindow.endDate}`}`}>{value.toLocaleString('zh-CN')}</span>
      },
    },
    {
      id: 'pddSales', label: '拼多多销量', width: 110, filterType: 'number',
      getValue: (record) => {
        const code = record.productCode.trim()
        if (!code || !pddSalesWindow) return null
        return pddSalesByProductCode.has(code) ? pddSalesByProductCode.get(code)! : (skusByProductCode.get(code)?.some((item) => item.platformId === 'pinduoduo') ? 0 : null)
      },
      render: (record) => {
        const code = record.productCode.trim()
        if (!code || !pddSalesWindow) {
          return <span title={pddOrders.loading ? '正在加载拼多多成交数据…' : (pddOrders.error || '拼多多成交数据暂不可用')}>—</span>
        }
        const hasPdd = pddSalesByProductCode.has(code) || skusByProductCode.get(code)?.some((item) => item.platformId === 'pinduoduo')
        if (!hasPdd) return <span title="未关联拼多多SKU">—</span>
        const value = pddSalesByProductCode.get(code) || 0
        return <span title={`拼多多确认成交件数 · ${salesPeriodLabel} · ${pddSalesWindow.startDate === pddSalesWindow.endDate ? pddSalesWindow.startDate : `${pddSalesWindow.startDate} 至 ${pddSalesWindow.endDate}`}`}>{value.toLocaleString('zh-CN')}</span>
      },
    },
    { id: 'barcode', label: '条码', width: 150, getValue: (record) => record.barcode, render: (record) => fieldValue(record.barcode) },
    { id: 'generalAgentPrice', label: '总代价', width: 100, filterType: 'number', getValue: (record) => record.generalAgentPrice, render: (record) => record.generalAgentPrice === null ? '—' : money(record.generalAgentPrice), summaryFormat: money },
    { id: 'brandSpecialPrice', label: '品牌特价', width: 110, filterType: 'number', getValue: (record) => companyCostFor(record).brandSpecialPrice, render: (record) => { const cost = companyCostFor(record); return cost.issue === 'special-price-conflict' ? '特价冲突' : cost.brandSpecialPrice === null ? '—' : money(cost.brandSpecialPrice) }, summaryFormat: money },
    { id: 'productCost', label: '产品成本', width: 110, filterType: 'number', getValue: currentProductCostFor, render: (record) => { const cost = companyCostFor(record); const value = currentProductCostFor(record); return cost.issue ? '成本冲突' : value === null ? '—' : money(value) }, summaryFormat: money },
    {
      id: 'inventoryCostMode', label: '库存成本口径', width: 130, getValue: (record) => inventoryCostModeLabel(currentInventoryModeFor(record)),
      render: (record) => {
        const draft = currentDraftFor(record)
        const cost = companyCostFor(record)
        return editUnlocked && draft ? <select value={draft.inventoryCostMode} disabled={cost.generalAgentPrice === null && cost.brandSpecialPrice === null} onChange={(event) => updateDraft(record.productCode, { inventoryCostMode: event.target.value as InventoryCostMode })}><option value="general-agent" disabled={cost.generalAgentPrice === null}>总代库存</option><option value="brand-special" disabled={cost.brandSpecialPrice === null}>特价库存</option></select> : <span className={`inventory-cost-label ${currentInventoryModeFor(record) === 'brand-special' ? 'is-special' : ''}`} title="总代/特价库存口径，只在本公司库维护">{inventoryCostModeLabel(currentInventoryModeFor(record))}</span>
      },
    },
    { id: 'accessory', label: '配件', width: 84, getValue: (record) => currentDraftFor(record)?.isAccessory ? '是' : '否', render: (record) => { const draft = currentDraftFor(record); return editUnlocked && draft ? <select value={draft.isAccessory ? 'yes' : 'no'} onChange={(event) => updateDraft(record.productCode, { isAccessory: event.target.value === 'yes' })}><option value="yes">是</option><option value="no">否</option></select> : <span className={`accessory-label ${draft?.isAccessory ? 'is-accessory' : ''}`} title="公司配件属性，只在本公司库维护">{draft?.isAccessory ? '是' : '否'}</span> } },
    { id: 'provincialAgentPrice', label: '新省代价', width: 100, filterType: 'number', getValue: (record) => record.provincialAgentPrice, render: (record) => record.provincialAgentPrice === null ? '—' : money(record.provincialAgentPrice), summaryFormat: money },
    { id: 'ecommercePrice', label: '电商价', width: 100, filterType: 'number', getValue: (record) => record.ecommercePrice, render: (record) => record.ecommercePrice === null ? '—' : money(record.ecommercePrice), summaryFormat: money },
    { id: 'ip', label: 'IP', width: 110, getValue: (record) => record.ip, render: (record) => fieldValue(record.ip) },
    { id: 'capacity', label: '容量', width: 90, filterType: 'number', getValue: (record) => record.capacity, render: (record) => fieldValue(record.capacity) },
    { id: 'brand', label: '品牌', width: 100, getValue: (record) => record.brand, render: (record) => fieldValue(record.brand) },
    { id: 'cupType', label: '杯型', width: 220, getValue: (record) => record.cupType, render: (record) => <span className="internal-wide-text" title={record.cupType}>{fieldValue(record.cupType)}</span> },
    { id: 'material', label: '材质', width: 100, getValue: (record) => record.material, render: (record) => fieldValue(record.material) },
    { id: 'audience', label: '人群', width: 90, getValue: (record) => record.audience, render: (record) => fieldValue(record.audience) },
    { id: 'recordDate', label: '日期', width: 120, filterType: 'date', getValue: (record) => record.recordDate, render: (record) => fieldValue(record.recordDate) },
    {
      id: 'platformMappings', label: platformMappings ? '平台规格映射' : '关联京东SKU', width: 180,
      getValue: (record) => linkedVariants(record).map((variant) => `${variant.platformId} ${variant.id}`).join(' '),
      render: (record) => {
        const linked = linkedVariants(record)
        return linked.length ? <span className="internal-linked-skus">{linked[0].platformId === 'jd' ? <Link to={`/product/sku/${linked[0].id}`}>京东 · {linked[0].id}</Link> : <span>{linked[0].platformId} · {linked[0].id}</span>}{linked.length > 1 && <small>+{linked.length - 1}</small>}</span> : <span className="internal-unmatched">未关联</span>
      },
    },
  ]

  return (
    <>
      <section className="product-master-summary internal-product-summary" aria-label="公司产品信息概览">
        <div><span>公司产品记录</span><strong>{internalProductInfo.summary.records.toLocaleString('zh-CN')}</strong></div>
        <div><span>唯一产品编码</span><strong>{internalProductInfo.summary.uniqueProductCodes.toLocaleString('zh-CN')}</strong></div>
        <div><span>已关联记录</span><strong>{matchedRecordCount.toLocaleString('zh-CN')}</strong></div>
        <div><span>已有代表图</span><strong>{imageRecordCount.toLocaleString('zh-CN')}</strong></div>
        <div><span>{platformMappings ? '已映射平台规格' : '关联京东SKU'}</span><strong>{linkedSkuCount.toLocaleString('zh-CN')}</strong></div>
      </section>

      <p className="internal-product-source">
        来源：{internalProductInfo.source.sheet}，共{internalProductInfo.summary.records.toLocaleString('zh-CN')}行；
        {platformMappings ? '按已确认的平台商品映射关联各店铺规格' : '按“产品编码 = 商家SKU”关联京东商品'}，未关联记录仍完整保留；
        公司品名、采购成本和属性以公司源表为准，公司上传图片优先，{hasJdReferenceSource ? '无公司图片时使用已关联京东SKU的参考图' : '京东SKU参考目录当前不可用'}；
        公司销量取自网店管家ERP全渠道口径（仅昨日/近7天/近30天快照；自定义区间显示为—），京东/拼多多销量为对应店铺确认成交件数，三者共用上方时间范围；
        已忽略{internalProductInfo.summary.invalidSpecialPrices}个无效特价值。
      </p>

      <div className="product-master-cost-note"><strong>公司主表编辑</strong><span>图片、配件属性和“总代/特价库存”口径只在这里维护；各店铺SKU表仅展示这些字段。</span></div>
      <div className="company-sales-period-bar">
        <div>
          <strong>销量统计范围</strong>
          <span>
            {salesPeriod === 'custom'
              ? '自定义区间：京东/拼多多按确认成交汇总；公司销量（ERP）无分天明细'
              : '公司销量＝ERP；京东/拼多多＝店铺确认成交件数'}
          </span>
        </div>
        <div className="ad-date-toolbar company-sales-date-toolbar">
          <div className="metric-switch" role="group" aria-label="销量统计范围">
            {COMPANY_SALES_PERIOD_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={salesPeriod === option.key ? 'is-active' : ''}
                onClick={() => {
                  setCalendarOpen(false)
                  setSalesPeriod(option.key)
                }}
              >
                {option.label}
              </button>
            ))}
            <button
              type="button"
              className={salesPeriod === 'custom' ? 'is-active' : ''}
              onClick={() => {
                if (!customRange.start || !customRange.end) {
                  const end = calendarLatestDate
                  const startDate = new Date(`${end}T00:00:00Z`)
                  startDate.setUTCDate(startDate.getUTCDate() - 6)
                  setCustomRange({
                    start: startDate.toISOString().slice(0, 10),
                    end,
                  })
                }
                setCalendarOpen(true)
              }}
            >
              {salesPeriod === 'custom' && customRange.start && customRange.end
                ? rangeDisplayLabel(customRange)
                : '自定义'}
            </button>
          </div>
          <DualMonthRangeCalendar
            open={calendarOpen}
            latestDate={calendarLatestDate}
            value={customRange.start && customRange.end
              ? customRange
              : { start: calendarLatestDate, end: calendarLatestDate }}
            onClose={() => setCalendarOpen(false)}
            onConfirm={(range) => {
              setCustomRange(range)
              setSalesPeriod('custom')
              setCalendarOpen(false)
            }}
          />
        </div>
      </div>
      {message && <p className="form-success">{message}</p>}
      {error && <p className="form-error">{error}</p>}
      {pddOrders.error && <p className="internal-product-source is-warning">拼多多成交数据加载失败，拼多多销量暂时显示为“—”：{pddOrders.error}</p>}
      {!productContext.loading && !data && <p className="internal-product-source is-warning">京东成交数据暂不可用，京东销量暂时显示为“—”。</p>}
      <DataTableWorkbench
        storageKey="company-product-library-workbench-v1"
        columns={companyColumns}
        rows={companyRows}
        rowKey={(record) => String(record.sourceRow)}
        searchPlaceholder={platformMappings ? '搜索品名、产品编码、条码、IP或平台规格ID' : '搜索品名、产品编码、条码、IP或京东SKU'}
        emptyMessage="没有符合当前筛选条件的公司产品信息。"
        tableClassName="internal-product-table"
        continuous
        summary
        statusNote={<span>源表另有 {internalProductInfo.summary.recordsWithoutProductCode} 条记录缺少产品编码{erpInventory ? ` · ERP库存截至 ${erpInventory.source.exportedAt.replace('T', ' ').slice(0, 19)}` : ''}</span>}
        editAction={<button type="button" className={editUnlocked ? 'is-unlocked' : ''} onClick={() => void toggleEditLock()} disabled={saving} title={saving ? undefined : editUnlocked ? drafts.size ? `保存 ${drafts.size} 项修改并锁定公司字段` : '点击锁定公司字段' : '点击解锁公司字段'}>
          {editUnlocked ? <LockOpen aria-hidden="true" /> : <Lock aria-hidden="true" />}
          {saving ? '正在保存…' : editUnlocked ? drafts.size ? `解锁中可编辑 · 保存并锁定（${drafts.size}）` : '解锁中可编辑' : '锁定中不可编辑'}
        </button>}
      />
    </>
  )
}
